"use client";

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity, DollarSign, Zap, MessageSquare } from 'lucide-react';

interface AIUsageSummaryProps {
  timeRange: '24h' | '7d' | '30d' | 'all';
  onTimeRangeChange: (range: '24h' | '7d' | '30d' | 'all') => void;
}

export function AIUsageSummary({ timeRange, onTimeRangeChange }: AIUsageSummaryProps) {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['ai-usage-summary', timeRange],
    queryFn: async () => {
      let query = supabase
        .from('ai_conversation_logs')
        .select('*');
      
      // Apply time filter
      if (timeRange !== 'all') {
        const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
        const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
        query = query.gte('created_at', since);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      // Calculate aggregates
      const totalTokens = data.reduce((sum, log) => sum + (log.input_tokens || 0) + (log.output_tokens || 0), 0);
      const totalCost = data.reduce((sum, log) => sum + (log.estimated_cost_cents || 0), 0);
      const totalCacheRead = data.reduce((sum, log) => sum + (log.cache_read_tokens || 0), 0);
      const totalInput = data.reduce((sum, log) => sum + (log.input_tokens || 0), 0);
      const cacheHitRate = totalInput > 0 ? (totalCacheRead / totalInput) * 100 : 0;
      const uniqueConversations = new Set(data.map(log => log.conversation_id)).size;
      
      return {
        totalTokens,
        totalCost: totalCost / 100, // Convert cents to dollars
        cacheHitRate,
        uniqueConversations,
      };
    },
  });

  const hasData = summary && (summary.totalTokens > 0 || summary.uniqueConversations > 0);

  const stats = [
    {
      title: 'Total Tokens',
      value: isLoading ? '...' : (summary?.totalTokens || 0).toLocaleString(),
      icon: Activity,
      description: hasData ? 'Input + Output tokens' : 'No data yet',
    },
    {
      title: 'Total Cost',
      value: isLoading ? '...' : `$${(summary?.totalCost || 0).toFixed(2)}`,
      icon: DollarSign,
      description: hasData ? 'Estimated API cost' : 'No data yet',
    },
    {
      title: 'Cache Hit Rate',
      value: isLoading ? '...' : `${(summary?.cacheHitRate || 0).toFixed(1)}%`,
      icon: Zap,
      description: hasData ? 'Tokens from cache' : 'No data yet',
    },
    {
      title: 'Conversations',
      value: isLoading ? '...' : (summary?.uniqueConversations || 0).toLocaleString(),
      icon: MessageSquare,
      description: hasData ? 'Unique conversations' : 'Start using AI Assistant',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Usage Summary</h2>
        <Select value={timeRange} onValueChange={(value: any) => onTimeRangeChange(value)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24 Hours</SelectItem>
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
